import { ChevronDown, KeyRound, LogOut, UserRoundCog } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Avatar } from './Avatar';
import { LAYERS, anchorTo, portalHost, type Placement } from '../lib/floating';

/**
 * Everything about "who am I signed in as", behind one control.
 *
 * ## What this replaces
 *
 * The header carried seven things at once: a greeting, today's date, the
 * assistant, the notification bell, the account name, the role, "Switch
 * account" and "Sign out". Every one of them was defensible on its own and
 * together they were the busiest strip on the screen — above a page whose own
 * title then had to compete with them.
 *
 * Only two of those seven are used more than once a day. The rest describe the
 * session, and a session needs saying once, in one place, when somebody asks.
 * So they live here, behind a control that shows the answer to the only
 * question the header was really answering — *whose seat is this?* — and hides
 * the rest until pressed.
 *
 * ## Why the greeting survives inside it
 *
 * It was never decoration. An admin and a dispatcher sharing an office machine
 * is the case this application actually has, and "Good afternoon, Mercy" on a
 * screen Mercy did not sign into is the fastest way anybody notices. Moving it
 * into the menu keeps that, at the cost of a click, and buys back the whole
 * header strip.
 *
 * ## Why it portals
 *
 * The header is `z-30` chrome and creates a stacking context. A dropdown that
 * is merely `absolute` inside it is clipped by the header's own bounds, which
 * on this codebase has already shipped once as an overlay painted over by the
 * footer. Every floating thing here goes through `portalHost()`.
 */
export function AccountMenu({
  user,
  greeting,
  today,
  onSwitch,
  onSignOut,
}: {
  user: { id?: string; name?: string; role?: string; email?: string } | null;
  greeting: string;
  today: string;
  onSwitch: () => void;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<Placement | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  /*
   * Measure after paint, not during. The panel has to exist before its height
   * is known, so the first pass places it provisionally and this corrects it —
   * the same two-step every floating panel here uses.
   */
  useLayoutEffect(() => {
    if (!open || !button.current) return;
    const anchor = button.current.getBoundingClientRect();
    const size = panel.current?.getBoundingClientRect() ?? { width: 260, height: 240 };
    setAt(anchorTo(anchor, { width: size.width, height: size.height }, 'bottom'));
  }, [open]);

  /* Escape closes it, and a click anywhere outside does too. Without the first
     a keyboard user is trapped; without the second the menu outstays its use. */
  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!panel.current?.contains(target) && !button.current?.contains(target)) setOpen(false);
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const go = (where: string) => {
    setOpen(false);
    navigate(where);
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex min-h-tap items-center gap-2 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-muted"
      >
        <Avatar userId={user?.id} name={user?.name ?? 'You'} size="md" />
        <span className="hidden min-w-0 sm:block">
          <span className="block truncate text-sm font-medium leading-tight">{user?.name}</span>
          <span className="block truncate text-xs capitalize text-muted-foreground">
            {user?.role}
          </span>
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
        <span className="sr-only">Your account and session</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={panel}
            role="menu"
            aria-label="Your account"
            style={{
              position: 'fixed',
              top: at?.top ?? 0,
              left: at?.left ?? 0,
              zIndex: LAYERS.flyout,
              // Placed off-screen for the measuring pass, so nothing flashes in
              // the wrong corner before it is positioned.
              visibility: at ? 'visible' : 'hidden',
            }}
            className="w-64 overflow-hidden rounded-2xl border border-border bg-card shadow-card"
          >
            <div className="border-b border-border px-3 py-2.5">
              <p className="text-sm font-medium">
                {greeting}, {user?.name?.split(' ')[0] ?? 'there'}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">{today}</p>
              {user?.email && (
                <p className="mt-1 truncate text-xs text-muted-foreground">{user.email}</p>
              )}
              {/*
                Which role, moved up from the footer.

                It is the thing people get wrong on a shared office machine and
                the first question support asks, and it belongs where somebody
                looks for who they are — not at the bottom of a page they would
                have to scroll to reach.
              */}
              {user?.role && (
                <p className="mt-1 text-xs capitalize text-muted-foreground">
                  Signed in as {user.role}
                </p>
              )}
            </div>

            <div className="p-1">
              <button
                type="button"
                role="menuitem"
                onClick={() => go('/account')}
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-muted"
              >
                <KeyRound className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                Your account and password
              </button>

              {/*
                Handing the machine to somebody else.

                One browser holds one session, so a colleague signing in ends
                yours. This is the polite way to do the same thing on purpose:
                it signs the current person out and says whose seat has been
                vacated, so the next person is not left guessing whether they
                are about to work as somebody else.
              */}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onSwitch();
                }}
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-muted"
              >
                <UserRoundCog className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                Switch account
              </button>

              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onSignOut();
                }}
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-muted"
              >
                <LogOut className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                Sign out
              </button>
            </div>
          </div>,
          portalHost(),
        )}
    </>
  );
}
